import React, { useState } from 'react';
import './BillingAddressForm.css';

// List of US states with their abbreviations
const US_STATES = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' }
];

function BillingAddressForm({ 
  savedAddress, 
  onAddressChange, 
  onSaveAddressChange,
  showSaveAddressOption,
  forceSaveAddress,
  onClose
}) {
  const [formData, setFormData] = useState({
    line1: savedAddress?.line1 || '',
    line2: savedAddress?.line2 || '',
    city: savedAddress?.city || '',
    state: savedAddress?.state || '',
    postal_code: savedAddress?.postal_code || '',
    country: savedAddress?.country || 'US'
  });

  const [errors, setErrors] = useState({});
  const [shouldSaveAddress, setShouldSaveAddress] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.line1.trim()) newErrors.line1 = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.postal_code.trim()) newErrors.postal_code = 'ZIP code is required';
    
    // Validate state is a valid US state
    const isValidState = US_STATES.some(state => 
      state.abbr === formData.state.toUpperCase() || 
      state.name.toLowerCase() === formData.state.toLowerCase()
    );
    if (!isValidState) {
      newErrors.state = 'Please select a valid US state';
    }
    
    // Basic ZIP code validation for US
    if (formData.country === 'US' && !/^\d{5}(-\d{4})?$/.test(formData.postal_code)) {
      newErrors.postal_code = 'Please enter a valid ZIP code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onAddressChange(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="billing-address-form">
      <div className="form-group">
        <label htmlFor="line1">Street Address</label>
        <input
          type="text"
          id="line1"
          name="line1"
          value={formData.line1}
          onChange={handleChange}
          className={errors.line1 ? 'error' : ''}
          placeholder="123 Main St"
        />
        {errors.line1 && <span className="error-message">{errors.line1}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="line2">Apartment, suite, etc. (optional)</label>
        <input
          type="text"
          id="line2"
          name="line2"
          value={formData.line2}
          onChange={handleChange}
          placeholder="Apt 4B"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={errors.city ? 'error' : ''}
            placeholder="New York"
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="state">State</label>
          <input
            type="text"
            id="state"
            name="state"
            list="state-list"
            value={formData.state}
            onChange={handleChange}
            className={errors.state ? 'error' : ''}
            placeholder="NY"
            autoComplete="off"
          />
          <datalist id="state-list">
            {US_STATES.map(state => (
              <option key={state.abbr} value={state.abbr}>
                {state.name}
              </option>
            ))}
          </datalist>
          {errors.state && <span className="error-message">{errors.state}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="postal_code">ZIP Code</label>
          <input
            type="text"
            id="postal_code"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            className={errors.postal_code ? 'error' : ''}
            placeholder="10001"
          />
          {errors.postal_code && <span className="error-message">{errors.postal_code}</span>}
        </div>
      </div>

      {showSaveAddressOption && !forceSaveAddress && (
        <div className="save-address-option">
          <label>
            <input
              type="checkbox"
              checked={shouldSaveAddress}
              onChange={(e) => {
                setShouldSaveAddress(e.target.checked);
                onSaveAddressChange(e.target.checked);
              }}
            />
            Save billing address for future purchases
          </label>
        </div>
      )}

      {forceSaveAddress && (
        <div className="save-address-notice">
          <p>
            <strong>Note:</strong> Your billing address will be saved for future recurring payments.
          </p>
        </div>
      )}

      <button type="submit" className="save-address-button">
        {forceSaveAddress ? 'Save & Use This Address for Recurring Payments' : 
         shouldSaveAddress ? 'Save & Use This Address' : 
         'Use This Address'}
      </button>
    </form>
  );
}

export default BillingAddressForm; 